import { useState } from 'react'

import { Box, IconBadge, Text, useBreakpoint } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { AvailableModules } from 'generated/featureDefinitions'
import { ProductUpdatesPoliciesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useGetProductUpdates } from 'modules/product_updates/hooks/useGetProductUpdates'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

const HeaderStyle = {
  background:
    'radial-gradient(50% 140% at 50% -25%, rgb(250, 205, 210) 0%, rgb(255, 255, 255) 90%)',
  backgroundBlendMode: 'normal',
  marginBottom: '48px',
}

const UpdatesWithCEOHeader = () => {
  const { productUpdates, loading } = useGetProductUpdates({
    ids: ['cGv63qtYQcGopkKRVcJJMQ'], // Id of the entry in DatoCMS to search for
    preview: true,
  })
  const { data: upsellData } = useQuery(ProductUpdatesPoliciesDocument)

  const { breakpoint, isMobile } = useBreakpoint()
  const isMediumScreen = breakpoint === 'm'
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false)
  const canSeeUpsellButton = upsellData?.productUpdates._policies.upsellButtonSeeUpsellButton.can

  const session = useCurrentSession()

  const dataPerCountry = (country: string) => {
    switch (country) {
      case 'es':
        return {
          feature: 'benefits',
          title: i18n.t('product_updates.updates_with_ceo.landing.header.benefits.title'),
          description: i18n.t(
            'product_updates.updates_with_ceo.landing.header.benefits.description'
          ),
        }
      case 'de':
        return {
          feature: 'datev',
          title: i18n.t('product_updates.updates_with_ceo.landing.header.datev.title'),
          description: i18n.t('product_updates.updates_with_ceo.landing.header.datev.description'),
        }
      default:
        return null
    }
  }

  const data = dataPerCountry(session.company.legalEntities[0]?.country || '')

  const videoId = productUpdates.at(0)?.externalUrl?.split('v=')[1]?.split('&')[0]

  const moduleRequested = data?.feature

  return (
    <Box
      flexDirection={isMobile ? 'column' : 'row'}
      position="relative"
      justifyContent="spaceBetween"
      gap="s64"
      alignItems="center"
      style={HeaderStyle}
      paddingY="s40"
    >
      <Box gap="s24">
        <Box gap="s8">
          <span className="text-sm font-semibold text-f1-foreground-secondary ">
            {i18n.t('product_updates.updates_with_ceo.title').toUpperCase()}
          </span>
          <h1 className="text-4xl font-semibold text-f1-foreground ">
            {i18n.t('product_updates.updates_with_ceo.landing.header.subtitle')}
          </h1>
        </Box>
        {data && canSeeUpsellButton && (
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="flexStart"
            background="grey200"
            padding="s8"
            borderRadius={{ all: 'abs016' }}
            gap="s32"
            maxWidth="fit-content"
          >
            <Box flexDirection="row" gap="s8">
              <IconBadge icon="bell" color="grey200" iconColor="grey700" />
              <span className="text-lg font-semibold">{data.title}</span>
            </Box>

            <Button
              variant="promote"
              label={i18n.t('product_updates.updates_with_ceo.landing.learn_more')}
              onClick={() => setIsUpsellModalOpen(true)}
            />
          </Box>
        )}
      </Box>

      {productUpdates && !loading && videoId && (
        <Box
          className="aspect-video"
          style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : isMediumScreen ? '540px' : '840px',
            overflow: 'hidden',
          }}
          borderRadius={{
            all: 'abs016',
          }}
        >
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${videoId}`}
            allowFullScreen
          />
        </Box>
      )}

      {isUpsellModalOpen && (
        <RequestUpgradeModal
          requestReason={ValidReasons.MissingAddon}
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          moduleRequested={moduleRequested as AvailableModules | 'benefits' | 'datev'}
          requestSource="news_updates_with_ceo"
          title={i18n.t('product_updates.updates_with_ceo.landing.upsell_modal.title')}
          description={
            <Box gap="s16">
              <Text.Normal size="300" overflowWrap>
                {i18n.t('product_updates.updates_with_ceo.landing.upsell_modal.description')}
              </Text.Normal>
            </Box>
          }
          onClose={() => {
            setIsUpsellModalOpen(false)
          }}
        />
      )}
    </Box>
  )
}

export default UpdatesWithCEOHeader
